import { defineComponent as _defineComponent } from 'vue'
import MyProjectsContractEditHeadline from '@/views/my-projects/components/contract/MyProjectsContractEditHeadline.vue';
import { useToggle } from '@vueuse/core';
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import { computed, ref, watchEffect } from 'vue';
import { BcxFormField } from '@/models/BcxForm';
import ConfirmButtons from '@/components/molecules/ConfirmButtons.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import useI18n from '@/mixins/useI18n';
import { i18nToItemList } from '@/utils/selectItemTools';
import useBcxForm from '@/mixins/useBcxForm';
import useContract from '@/views/my-projects/composables/useContract';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import { useRoute } from 'vue2-helpers/vue-router';
import { postContractOffer, putContractOffer } from '@/services/ContractService';
import { useFormValidation } from '@/utils/bcxFormValidations';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsContractBaseDataOffer',
  setup(__props) {

const { t } = useI18n();
const { isMobileWidth } = useResponsiveness();
const [isEditing, toggleEdit] = useToggle(false);

const {
  contractDetail, isContractStatusOpen, offerData, isUserContractor
} = useContract();

const route = useRoute();
const localOfferData = ref({ ...offerData.value });

const IsThereAlreadyAnOffer = computed(() => !!contractDetail.value?.projectOffer?.lastUpdated);
const shouldEditButtonBeDisplayed = computed(() => !!(isUserContractor.value && isContractStatusOpen.value && contractDetail.value));

const { getFormattedDate } = useFormattedDateTime();

const { validateHrsPerWeek, validateRate } = useFormValidation();

const hrsPerWeekInputValidation = (value: string) => {
  if (localOfferData.value?.dependingOnComplexity) {
    return null;
  }

  return validateHrsPerWeek(value);
};

const offerFields = computed(() => {
  const fields: BcxFormField[] = [
    {
      type: 'select',
      label: `${t('contracts.contract.base-data.offer-labels.billing-option')}`,
      name: 'billingUnit',
      width: isMobileWidth.value ? '50%' : '27%',
      options: i18nToItemList('project-forum.apply-box.billing-options').value ?? [],
    },
    {
      type: 'number',
      label: `${t('contracts.contract.base-data.offer-labels.rate')}`,
      name: 'billingAmount',
      width: isMobileWidth.value ? '50%' : '27%',
      suffix: '€',
      fieldWidth: isMobileWidth.value ? '100%' : '65%',
      textOptions: {
        max: 999999.99,
        min: 1
      },
      validations: [
        validateRate
      ]
    },
    {
      type: 'date',
      label: `${t('contracts.contract.base-data.offer-labels.start')}`,
      name: 'startDate',
      width: isMobileWidth.value ? '50%' : '46%',
      fieldWidth: isMobileWidth.value ? '100%' : '63%',
      dateOptions: {
        secondary: !!localOfferData.value?.dependingOnComplexity,
        earliest: new Date().toISOString()
      },
    },
    {
      type: 'number',
      label: `${t('contracts.contract.base-data.offer-labels.hrs-week')}`,
      name: 'hoursPerWeek',
      width: isMobileWidth.value ? '50%' : '27%',
      disabled: !!localOfferData.value?.dependingOnComplexity,
      textOptions: {
        min: 1,
        max: 60
      },
      validations: [
        hrsPerWeekInputValidation
      ],
      triggerValidation: !!localOfferData.value?.dependingOnComplexity,
    },
    {
      type: 'checkbox',
      label: `${t('contracts.contract.base-data.offer-labels.depending-on-complexity')}`,
      name: 'dependingOnComplexity',
      width: isMobileWidth.value ? '50%' : '27%'
    },
    {
      type: 'select',
      label: `${t('contracts.contract.base-data.offer-labels.remote')}`,
      name: 'remoteWork',
      width: isMobileWidth.value ? '50%' : '46%',
      fieldWidth: isMobileWidth.value ? '100%' : '75%',
      options: i18nToItemList('project-forum.apply-box.remote-options').value ?? []
    },
  ];
  if (isMobileWidth.value) {
    fields.splice(3, 0, fields.pop());
  }
  return fields;
});

const offerChanged = computed(() => useFormattedDateTime().getFormattedDate((localOfferData.value?.lastUpdated ?? '')));

const {
  onBlur, errorMessages, hasErrors, validateAllFields
} = useBcxForm(localOfferData, offerFields);

function prepareUpdatePayload() {
  const offer = localOfferData.value;
  return {
    billingUnit: offer.billingUnit,
    billingAmount: offer.billingAmount,
    startDate: offer.startDate,
    hoursPerWeek: offer.hoursPerWeek,
    remoteWork: offer.remoteWork,
    validUntil: offer.validUntil,
    dependingOnComplexity: offer.dependingOnComplexity,
    message: offer.message,
  };
}

function prepareCreatePayload() {
  const offer = localOfferData.value;
  return {
    billingUnit: offer.billingUnit,
    billingAmount: offer.billingAmount,
    startDate: offer.startDate,
    hoursPerWeek: offer.hoursPerWeek,
    remoteWork: offer.remoteWork,
    validUntil: offer.validUntil,
    dependingOnComplexity: offer.dependingOnComplexity,
    message: offer.message,
    startDateChanged: offer.startDateChanged,
    hoursPerWeekChanged: offer.hoursPerWeekChanged,
    remoteWorkChanged: offer.remoteWorkChanged,
  };
}

async function updateOffer() {
  await putContractOffer(route.params.contractId, prepareUpdatePayload());
}

async function createOffer() {
  await postContractOffer(route.params.contractId, prepareCreatePayload());
}

const onSave = async () => {
  validateAllFields();
  if (hasErrors.value) {
    return;
  }
  isEditing.value = false;

  try {
    if (!contractDetail.value?.projectOffer?.id) {
      await createOffer();
    } else await updateOffer();
    localOfferData.value.lastUpdated = new Date().toISOString();
    offerData.value = localOfferData.value;
  } catch (e: any) {
    const translatedError = new TranslatedBackendErrors(e.response);
    translatedError.outputGlobalErrorMessage('MyProjectsContractBaseDataOffer.vue');
  }
};

const onCancel = () => {
  isEditing.value = false;
  localOfferData.value = { ...offerData.value };
};

// If reactive data changes, it is updated
watchEffect(() => {
  localOfferData.value = { ...offerData.value };
});

return { __sfc: true,t, isMobileWidth, isEditing, toggleEdit, contractDetail, isContractStatusOpen, offerData, isUserContractor, route, localOfferData, IsThereAlreadyAnOffer, shouldEditButtonBeDisplayed, getFormattedDate, validateHrsPerWeek, validateRate, hrsPerWeekInputValidation, offerFields, offerChanged, onBlur, errorMessages, hasErrors, validateAllFields, prepareUpdatePayload, prepareCreatePayload, updateOffer, createOffer, onSave, onCancel, MyProjectsContractEditHeadline, BCXForm, ConfirmButtons }
}

})